import React, { memo, useEffect, useState } from 'react';

// Libraries
import { Form, Formik } from 'formik';
import { AnimatePresence, m } from 'framer-motion';
import { PropTypes } from "prop-types";
import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

// Components
import { resetForm } from '../../Functions/Utilities';
import { Input } from '../Form/Form';
import SocialIcons from '../SocialIcon/SocialIcons';
import { Footer } from './Footer';

// Firebase
import { db } from '../../config/firebase';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';

// Toast notifications
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// i18n
import { useTranslation } from 'react-i18next';

const footerStyles = {
  wrapper: {
    background: 'linear-gradient(180deg, #111122 0%, #1a1a2e 100%)',
    padding: '80px 0 40px',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  logo: {
    maxWidth: '160px',
    height: 'auto',
    marginBottom: '20px',
    filter: 'brightness(0) invert(1)',
    opacity: 0.9,
  },
  tagline: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '14px',
    lineHeight: 1.7,
    marginBottom: '24px',
    maxWidth: '280px',
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: 600,
    marginBottom: '20px',
    letterSpacing: '0.5px',
  },
  link: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '14px',
    textDecoration: 'none',
    display: 'block',
    marginBottom: '12px',
    transition: 'color 0.2s ease',
  },
  subscribeText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '14px',
    lineHeight: 1.6,
    marginBottom: '20px',
  },
  subscribeInput: {
    width: '100%',
    padding: '12px 16px',
    paddingRight: '52px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  subscribeBtn: {
    position: 'absolute',
    right: '6px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: '#D4A853',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 14px',
    color: '#1a1a2e',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  divider: {
    borderTop: '1px solid rgba(255,255,255,0.08)',
    marginTop: '48px',
    paddingTop: '24px',
  },
  copyright: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '13px',
    textAlign: 'center',
    margin: 0,
  },
};

const FooterStyle04 = (props) => {
  const { t } = useTranslation();
  const [iconData, setIconData] = useState([]);

  const footerLinks = [
    { title: t('footer.home'), link: '/' },
    { title: t('footer.aboutUs'), link: '/wichtigeinfo' },
    { title: t('footer.programme'), link: '/allnews' },
    { title: t('footer.contactLink'), link: '/contactus' },
    { title: t('footer.privacy'), link: '/privacy' },
  ];

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "socialLinks"));
        if (!querySnapshot.empty) {
          const data = querySnapshot.docs[0].data();
          const icons = [];
          if (data.youtube || data.facebook) {
            icons.push({ color: "#FF0000", link: data.youtube || data.facebook, icon: "fab fa-youtube" });
          }
          if (data.instagram) {
            icons.push({ color: "#E4405F", link: data.instagram, icon: "fab fa-instagram" });
          }
          setIconData(icons);
        }
      } catch (error) {
        console.error("Error fetching social links:", error);
      }
    };

    fetchSocialLinks();
  }, []);

  const midIndex = Math.ceil(footerLinks.length / 2);
  const firstColumnLinks = footerLinks.slice(0, midIndex);
  const secondColumnLinks = footerLinks.slice(midIndex);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubscription = async (email, actions) => {
    if (!validateEmail(email)) {
      toast.warn(t('validation.validEmail'));
      actions.setSubmitting(false);
      return;
    }

    try {
      const subscribersRef = collection(db, "subscribers");
      const q = query(subscribersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        toast.warn(t('validation.alreadySubscribed'));
      } else {
        await addDoc(subscribersRef, {
          email,
          createdAt: new Date()
        });
        toast.success(t('validation.subscriptionSuccess'));
        resetForm(actions);
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error(t('validation.subscriptionFailed'));
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Footer topSpace={false} theme={props.theme} className={`footer-style-04${props.className ? ` ${props.className}` : ""}`}>
      <div style={footerStyles.wrapper}>
        <Container>
          <Row>
            {/* Logo & Tagline */}
            <Col lg={3} sm={6} className="mb-10 sm:mb-8">
              <Link aria-label="link" to="/" className="block">
                <img
                  style={footerStyles.logo}
                  src="/assets/img/webp/logo02.png"
                  alt="Pambazuka Events"
                />
              </Link>
              <p style={footerStyles.tagline}>
                {t('footer.tagline')}
              </p>
              <SocialIcons
                theme="social-icon-style-01"
                className="justify-start"
                size="md"
                iconColor="light"
                data={iconData.filter(icon => icon.link)}
              />
            </Col>

            {/* Quick Links */}
            <Col lg={3} sm={6} className="mb-10 sm:mb-8">
              <span style={footerStyles.sectionTitle}>{t('footer.quickLinks')}</span>
              <Row>
                <Col xs={6}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {firstColumnLinks.map((page, index) => (
                      <li key={index}>
                        <Link to={page.link} style={footerStyles.link}
                          onMouseEnter={e => e.target.style.color = '#D4A853'}
                          onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
                        >
                          {page.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Col>
                <Col xs={6}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {secondColumnLinks.map((page, index) => (
                      <li key={index}>
                        <Link to={page.link} style={footerStyles.link}
                          onMouseEnter={e => e.target.style.color = '#D4A853'}
                          onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
                        >
                          {page.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Col>
              </Row>
            </Col>

            {/* Newsletter */}
            <Col lg={{ span: 4, offset: 2 }} sm={6}>
              <span style={footerStyles.sectionTitle}>{t('footer.stayUpdated')}</span>
              <p style={footerStyles.subscribeText}>
                {t('footer.subscribeText')}
              </p>
              <Formik
                initialValues={{ email: "" }}
                validationSchema={Yup.object().shape({ email: Yup.string().email("Invalid email.").required("Field is required.") })}
                onSubmit={async (values, actions) => {
                  await handleSubscription(values.email, actions);
                }}
              >
                {({ isSubmitting, status, handleChange, handleBlur, values }) => (
                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <Form style={{ position: 'relative' }}>
                      <Input
                        showErrorMsg={false}
                        type="email"
                        name="email"
                        labelClass="w-full"
                        className="border-0 bg-transparent text-white py-[12px] pl-[16px] pr-[60px] w-full small-input"
                        style={footerStyles.subscribeInput}
                        placeholder={t('footer.enterEmail')}
                      />
                      <button
                        aria-label="subscribe"
                        type="submit"
                        style={footerStyles.subscribeBtn}
                        className={isSubmitting ? " loading" : ""}
                        onMouseEnter={e => e.target.style.backgroundColor = '#c49a47'}
                        onMouseLeave={e => e.target.style.backgroundColor = '#D4A853'}
                      >
                        <i className="feather-send text-lg m-0" style={{ color: '#1a1a2e' }}></i>
                      </button>
                    </Form>
                    <AnimatePresence>
                      {status && (
                        <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          className="absolute top-[115%] left-0 w-full" />
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </Formik>
            </Col>
          </Row>

          {/* Copyright */}
          <div style={footerStyles.divider}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <p style={footerStyles.copyright}>
                &copy; {new Date().getFullYear()} {t('footer.copyright')}
              </p>
              <Link
                to="/login"
                style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', textDecoration: 'none', transition: 'color 0.2s ease' }}
                onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.6)'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.3)'}
              >
                {t('footer.login')}
              </Link>
            </div>
          </div>
        </Container>
      </div>
      <ToastContainer />
    </Footer>
  );
};

FooterStyle04.propTypes = {
  className: PropTypes.string,
  logo: PropTypes.string,
};

export default memo(FooterStyle04);
